import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Page } from '../components/Layout.jsx';
import { Alert, BackLink, ProgressBar, StepHeader, TrustRow } from '../components/ui.jsx';
import PostcodeStep from '../steps/PostcodeStep.jsx';
import AddressStep from '../steps/AddressStep.jsx';
import QuestionStep from '../steps/QuestionStep.jsx';
import LoaderStep from '../steps/LoaderStep.jsx';
import OffersStep from '../steps/OffersStep.jsx';
import ThanksStep from '../steps/ThanksStep.jsx';
import NotFoundPage from './NotFoundPage.jsx';
import { getJourney, submitLead, trackStep, ApiError } from '../lib/api.js';
import { getAttribution, getSessionUid } from '../lib/attribution.js';
import { offersForBrand } from '../lib/offers.js';
import { BRAND_COPY, PROGRESS, isQuestionSkipped, questionsForBrand } from '../lib/questions.js';

/**
 * One component drives every journey. /ggbb2, /ggbb3 and anything added later
 * are the same code with a different row in the `journeys` table — which is
 * what makes launching a new Google Ads landing path a config change rather
 * than a deploy.
 *
 * The step order is derived from the brand rather than hardcoded, because the
 * home and business flows ask different questions in the middle while sharing
 * the postcode, address, offers and thank-you screens.
 */
export default function JourneyPage({ slug: slugProp }) {
  const params = useParams();
  const location = useLocation();
  const slug = slugProp ?? params.slug ?? 'home';

  // A postcode carried over from the homepage hero. Read once into state so a
  // later re-render (or the customer coming back to step one) does not re-run
  // the lookup against a stale value.
  const [seededPostcode] = useState(() => location.state?.postcode ?? '');

  const [journey, setJourney] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [step, setStep] = useState('postcode');
  // Distinguishes the initial page view from a return to step one, which is
  // what decides whether the postcode field is allowed to grab focus.
  const [returnedToPostcode, setReturnedToPostcode] = useState(false);
  const [postcode, setPostcode] = useState(seededPostcode);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [answers, setAnswers] = useState({});
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  const headingRef = useRef(null);
  const sessionUid = getSessionUid();

  const brand = journey?.brand ?? 'home';
  const copy = BRAND_COPY[brand] ?? BRAND_COPY.home;
  const questions = useMemo(() => questionsForBrand(brand), [brand]);
  const offers = useMemo(() => offersForBrand(brand), [brand]);

  /** The live step order, with any branch-skipped question removed. */
  const order = useMemo(
    () => [
      'postcode',
      'address',
      ...questions.filter((q) => !isQuestionSkipped(q, answers)).map((q) => q.key),
      'loader',
      'offers',
      'thanks',
    ],
    [questions, answers],
  );

  useEffect(() => {
    const controller = new AbortController();
    getJourney(slug, controller.signal)
      .then(setJourney)
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setLoadError(err instanceof ApiError && err.status === 404 ? 'not_found' : 'error');
      });
    return () => controller.abort();
  }, [slug]);

  const track = useCallback(
    (nextStep) => {
      trackStep({
        sessionUid,
        journeySlug: slug,
        step: nextStep,
        attribution: getAttribution(),
      });
    },
    [sessionUid, slug],
  );

  // The journey fetch can resolve after a fast customer has already submitted
  // their postcode. Firing 'landing' at that point would drag current_step
  // backwards, so it only fires while the customer is genuinely still on the
  // first step.
  const landingTracked = useRef(false);
  useEffect(() => {
    if (journey && !landingTracked.current && step === 'postcode') {
      landingTracked.current = true;
      track('landing');
    }
  }, [journey, step, track]);

  // Move focus to the new heading on every step change so keyboard and
  // screen-reader users are not left at the top of the document.
  useEffect(() => {
    if (step !== 'postcode') headingRef.current?.focus();
  }, [step]);

  const goTo = useCallback(
    (next) => {
      setStep(next);
      track(next);
    },
    [track],
  );

  const goBack = useCallback(() => {
    const index = order.indexOf(step);
    const previous = order[index - 1];
    if (!previous) return;
    if (previous === 'postcode') setReturnedToPostcode(true);
    // Never land back on the interstitial: it would immediately push forward
    // again and trap the customer on the offers screen.
    goTo(previous === 'loader' ? order[index - 2] : previous);
  }, [order, step, goTo]);

  const handlePostcodeFound = ({ postcode: found, addresses: list }) => {
    setPostcode(found);
    setAddresses(list);
    goTo('address');
  };

  const handleAddressSelected = (address) => {
    setSelectedAddress(address);
    goTo(order[order.indexOf('address') + 1]);
  };

  const handleAnswer = (key, value) => {
    // The next step is computed against the answers that include this one, so
    // answering "no broadband" skips the provider question in the same tick
    // rather than one step late.
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);

    const nextOrder = [
      'postcode',
      'address',
      ...questions.filter((q) => !isQuestionSkipped(q, nextAnswers)).map((q) => q.key),
      'loader',
      'offers',
      'thanks',
    ];
    goTo(nextOrder[nextOrder.indexOf(key) + 1]);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await submitLead(slug, {
        sessionUid,
        postcode,
        addressId: selectedAddress.id,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        // The qualifying answers ride along in the free-form `answers` object
        // the API already accepts, so no schema change is needed to add or
        // reorder a question.
        answers: {
          ...answers,
          ...(selectedOffer
            ? { offerId: selectedOffer.id, offerName: selectedOffer.name }
            : {}),
        },
        consentMarketing: values.consentMarketing,
        consentTerms: values.consentTerms,
        consentText: values.consentText,
        website: values.website || undefined,
        attribution: getAttribution(),
      });

      setResult({ reference: response.reference, firstName: values.firstName });
      goTo('thanks');
    } catch (err) {
      // Surface the first field-level message where there is one — it is far
      // more actionable than a generic failure.
      const fieldMessage =
        err instanceof ApiError ? Object.values(err.fieldErrors)[0] : undefined;
      setSubmitError(fieldMessage ?? err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // An unknown or switched-off slug is far more likely to be paid traffic than
  // a typo, so it gets the real 404 page rather than a bare heading.
  if (loadError === 'not_found') return <NotFoundPage reason="inactive" />;

  const question = questions.find((q) => q.key === step);
  const showBack = step !== 'postcode' && step !== 'loader' && step !== 'thanks';
  const showProgress = step !== 'loader' && step !== 'thanks';

  const title = {
    postcode: copy.postcodeTitle,
    address: copy.addressTitle,
    loader: 'Searching for offers...',
    offers: 'Offers Found!',
    thanks: 'Thank you!',
  }[step] ?? question?.title;

  const subtitle = {
    postcode:
      'In order to find the best deals available in your area we need to know a bit more about you.',
    address:
      'In order to find the best deals available in your area we need to know a bit more about you.',
    loader: 'Please hold a moment while we find the best deals for you.',
    offers: 'We’ve found some great offers to suit your situation.',
    thanks: 'Someone will be in touch shortly to discuss your options',
  }[step] ?? question?.subtitle;

  return (
    <Page brand={brand}>
      <section className="step">
        {showBack && (
          <div className="step__back">
            <BackLink onClick={goBack} />
          </div>
        )}

        <StepHeader ref={headingRef} eyebrow={copy.eyebrow} title={title} subtitle={subtitle} />

        {showProgress && (
          <ProgressBar value={PROGRESS[step] ?? 0} complete={step === 'offers'} />
        )}

        {loadError === 'error' && (
          <Alert>We are having trouble loading this page. Please refresh and try again.</Alert>
        )}

        <div className="step__body">
          {step === 'postcode' && (
            <PostcodeStep
              journey={journey}
              initialPostcode={postcode}
              onFound={handlePostcodeFound}
              autoFocus={returnedToPostcode}
              // Only on the first arrival: coming back to step one must not
              // fire the lookup again under the customer.
              autoSubmit={Boolean(seededPostcode) && !returnedToPostcode}
            />
          )}

          {step === 'address' && (
            <AddressStep
              postcode={postcode}
              addresses={addresses}
              onSelect={handleAddressSelected}
              onBack={goBack}
            />
          )}

          {question && (
            <QuestionStep question={question} value={answers[question.key]} onAnswer={handleAnswer} />
          )}

          {step === 'loader' && <LoaderStep onDone={() => goTo('offers')} />}

          {step === 'offers' && selectedAddress && (
            <OffersStep
              offers={offers}
              formTitle={copy.offersFormTitle}
              selectedOffer={selectedOffer}
              onChooseOffer={setSelectedOffer}
              onSubmit={handleSubmit}
              submitting={submitting}
              submitError={submitError}
            />
          )}

          {step === 'thanks' && <ThanksStep reference={result?.reference} />}
        </div>

        {step !== 'loader' && <TrustRow />}
      </section>
    </Page>
  );
}
